import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SettingsProvider } from "./lib/settings";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { WhatsAppFloat, MobileActionBar } from "./components/Floaters";
import HomePage from "./pages/HomePage";
import AuthCallback from "./components/AuthCallback";
import CookieConsent from "./components/CookieConsent";
import SeoRuntime from "./components/SeoRuntime";
import { initConsent } from "./lib/consent";
import { captureAttribution } from "./lib/attribution";
import { CustomerAuthProvider } from "./lib/customerAuth";

// Code splitting per rotta: homepage eager (LCP), tutto il resto lazy
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ChiSiamoPage = lazy(() => import("./pages/SimplePages").then((m) => ({ default: m.ChiSiamoPage })));
const LegalPage = lazy(() => import("./pages/SimplePages").then((m) => ({ default: m.LegalPage })));
const NotFoundPage = lazy(() => import("./pages/SimplePages").then((m) => ({ default: m.NotFoundPage })));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AreaClientiPage = lazy(() => import("./pages/AreaClientiPage"));
const CaseDetailPage = lazy(() => import("./pages/CaseDetailPage"));
const PlusPage = lazy(() => import("./pages/PlusPage"));
const PurchasePage = lazy(() => import("./pages/PurchasePage"));
const PaymentOutcomePage = lazy(() => import("./pages/PaymentOutcomePage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const QuotePublicPage = lazy(() => import("./pages/QuotePublicPage"));
const ChatWidget = lazy(() => import("./components/ChatWidget"));
const SearchOverlay = lazy(() => import("./components/SearchOverlay").then((m) => ({ default: m.SearchOverlay })));
const ToolsHome = lazy(() => import("./pages/tools/ToolsHome"));
const ColfCalculator = lazy(() => import("./pages/tools/ColfCalculator"));
const DeductionsTool = lazy(() => import("./pages/tools/DeductionsTool"));
const PensionTool = lazy(() => import("./pages/tools/PensionTool"));
const ForfettarioTool = lazy(() => import("./pages/tools/ForfettarioTool"));
const DeadlineTool = lazy(() => import("./pages/tools/DeadlineTool"));
const SuccessionChecklistTool = lazy(() => import("./pages/tools/SuccessionChecklistTool"));
const RentalContractTool = lazy(() => import("./pages/tools/RentalContractTool"));
const ServiceFinderTool = lazy(() => import("./pages/tools/ServiceFinderTool"));
const LocalPage = lazy(() => import("./pages/LocalPage"));
const ForeignersHubPage = lazy(() => import("./pages/ForeignersHubPage"));
const ZonePage = lazy(() => import("./pages/ZonePage"));

// skeleton con layout realistico: evita CLS mentre il chunk della rotta si carica
const RouteFallback = () => (
  <div className="pt-[72px] bg-white" aria-hidden="true">
    <div className="bg-[#171717] h-[260px] sm:h-[300px]" />
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-4">
      <div className="h-5 w-2/3 bg-[#F0F0F0] rounded-lg animate-pulse" />
      <div className="h-4 w-full bg-[#F5F5F5] rounded-lg animate-pulse" />
      <div className="h-4 w-5/6 bg-[#F5F5F5] rounded-lg animate-pulse" />
      <div className="h-44 w-full bg-[#F5F5F5] rounded-2xl animate-pulse" />
    </div>
  </div>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Shell = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    initConsent();
    captureAttribution();
  }, []);
  // Google OAuth: session_id in URL fragment — process before any route renders
  if (location.hash?.includes("session_id=")) {
    return (
      <div className="pb-[64px] lg:pb-0 bg-white min-h-screen">
        <main>
          <AuthCallback />
        </main>
        <Toaster position="bottom-right" richColors />
      </div>
    );
  }
  return (
    <div className="pb-[64px] lg:pb-0 bg-white min-h-screen">
      <ScrollToTop />
      <Header onSearch={() => setSearchOpen(true)} />
      <Suspense fallback={null}>
        <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      </Suspense>
      <Suspense fallback={<RouteFallback />}>
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/servizi" element={<ServicesPage />} />
          <Route path="/servizi/:slug" element={<ServicePage />} />
          <Route path="/categoria/:slug" element={<CategoryPage />} />
          <Route path="/privati-famiglie" element={<CategoryPage fixedSlug="privati-famiglie" />} />
          <Route path="/casa-locazioni" element={<CategoryPage fixedSlug="casa-locazioni" />} />
          <Route path="/lavoro-domestico" element={<CategoryPage fixedSlug="lavoro-domestico" />} />
          <Route path="/pensioni-previdenza" element={<CategoryPage fixedSlug="pensioni-previdenza" />} />
          <Route path="/partite-iva-professionisti" element={<CategoryPage fixedSlug="partite-iva-professionisti" />} />
          <Route path="/imprese-societa" element={<CategoryPage fixedSlug="imprese-societa" />} />
          <Route path="/servizi-digitali" element={<CategoryPage fixedSlug="servizi-digitali" />} />
          <Route path="/notarile" element={<CategoryPage fixedSlug="notarile" />} />
          <Route path="/stranieri-italia" element={<CategoryPage fixedSlug="stranieri-italia" />} />
          <Route path="/servizi-stranieri-italia" element={<ForeignersHubPage />} />
          <Route path="/prenota" element={<BookingPage />} />
          <Route path="/strumenti" element={<ToolsHome />} />
          <Route path="/strumenti/calcolo-costo-colf-badante" element={<ColfCalculator />} />
          <Route path="/strumenti/cosa-posso-scaricare-730" element={<DeductionsTool />} />
          <Route path="/strumenti/calcolo-pensione" element={<PensionTool />} />
          <Route path="/strumenti/regime-forfettario" element={<ForfettarioTool />} />
          <Route path="/strumenti/scadenza-registrazione-contratto" element={<DeadlineTool />} />
          <Route path="/strumenti/checklist-successione" element={<SuccessionChecklistTool />} />
          <Route path="/strumenti/quale-contratto-locazione" element={<RentalContractTool />} />
          <Route path="/strumenti/quali-servizi-mi-servono" element={<ServiceFinderTool />} />
          <Route path="/730-roma" element={<LocalPage pageSlug="730-roma" />} />
          <Route path="/isee-roma" element={<LocalPage pageSlug="isee-roma" />} />
          <Route path="/successioni-roma" element={<LocalPage pageSlug="successioni-roma" />} />
          <Route path="/registrazione-contratto-locazione-roma" element={<LocalPage pageSlug="registrazione-contratto-locazione-roma" />} />
          <Route path="/colf-badanti-roma" element={<LocalPage pageSlug="colf-badanti-roma" />} />
          <Route path="/spid-roma" element={<LocalPage pageSlug="spid-roma" />} />
          <Route path="/firma-digitale-roma" element={<LocalPage pageSlug="firma-digitale-roma" />} />
          <Route path="/roma" element={<ZonePage zoneSlug="roma" />} />
          <Route path="/roma-sud" element={<ZonePage zoneSlug="roma-sud" />} />
          <Route path="/eur-torrino" element={<ZonePage zoneSlug="eur-torrino" />} />
          <Route path="/infernetto-casal-palocco-axa" element={<ZonePage zoneSlug="infernetto-casal-palocco-axa" />} />
          <Route path="/ostia" element={<ZonePage zoneSlug="ostia" />} />
          <Route path="/acilia" element={<ZonePage zoneSlug="acilia" />} />
          <Route path="/contatti" element={<ContactPage />} />
          <Route path="/chi-siamo" element={<ChiSiamoPage />} />
          <Route path="/accedi" element={<AuthPage />} />
          <Route path="/area-clienti" element={<AreaClientiPage />} />
          <Route path="/area-personale" element={<Navigate to="/area-clienti" replace />} />
          <Route path="/area-personale/abbonamento" element={<AreaClientiPage initialTab="abbonamento" />} />
          <Route path="/area-personale/profilo" element={<AreaClientiPage initialTab="profilo" />} />
          <Route path="/area-personale/pratiche/:caseId" element={<CaseDetailPage />} />
          <Route path="/reimposta-password" element={<ResetPasswordPage />} />
          <Route path="/flash-plus" element={<PlusPage />} />
          <Route path="/acquista/:slug" element={<PurchasePage />} />
          <Route path="/preventivo/:id" element={<QuotePublicPage />} />
          <Route path="/pagamento/esito" element={<PaymentOutcomePage />} />
          <Route path="/pagamento/annullato" element={<PaymentCancelPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/privacy" element={<LegalPage type="privacy" />} />
          <Route path="/cookie" element={<LegalPage type="cookie" />} />
          <Route path="/termini" element={<LegalPage type="termini" />} />
          <Route path="/condizioni-vendita" element={<LegalPage type="vendita" />} />
          <Route path="/termini-flash-plus" element={<LegalPage type="plus" />} />
          <Route path="/condizioni-flash-plus" element={<LegalPage type="plus" />} />
          <Route path="/rimborsi" element={<LegalPage type="rimborsi" />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      </Suspense>
      <CookieConsent />
      <WhatsAppFloat />
      <MobileActionBar />
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
      <SeoRuntime />
      <Toaster position="bottom-right" richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <SettingsProvider>
        <CustomerAuthProvider>
          <Shell />
        </CustomerAuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}

export default App;
